import React, { useState, useRef, useEffect } from 'react';
import axios from './axiosInstance';
import ProtectedComponent from './ProtectedComponent';
import './styles/LowesGatewayFwRevert.css';

const TARGET_FW = '1.3.2.0';
const FW_LABEL = 'NEWTON_STANDARD_M9 1.3.2.0 (downgrade)';
const ACTIVE_STATUSES = ['queued', 'enabling-ssh', 'pushing', 'running', 'rebooting', 'verifying', 'retrying'];

function LowesGatewayFwRevert() {
  const [ipText, setIpText] = useState('');
  const [rows, setRows] = useState([]);            // [{ ip, online, fwVersion, error }]
  const [selected, setSelected] = useState({});    // { ip: true }
  const [loadingState, setLoadingState] = useState(false);
  const [error, setError] = useState(null);
  const [job, setJob] = useState(null);            // polled FirmwarePushJob
  const pollRef = useRef(null);

  // Re-probe current gateway state. opts.silent = background refresh (no
  // spinner / no error wipe / keep the current selection); used to auto-update
  // the firmware column after a downgrade finishes.
  const probeState = async (opts = {}) => {
    if (!opts.silent) { setError(null); setLoadingState(true); }
    try {
      const res = await axios.post('/lowes-gateway/state', { ips: ipText });
      setRows(res.data.results || []);
      if (!opts.keepSelection) setSelected({});
    } catch (err) {
      if (!opts.silent) { setError(err.response?.data?.error || 'Failed to get gateway state'); setRows([]); }
    } finally {
      if (!opts.silent) setLoadingState(false);
    }
  };

  const getState = () => { setJob(null); return probeState(); };

  const isTarget = (fw) => typeof fw === 'string' && fw.includes(TARGET_FW);
  // Eligible for downgrade = reachable AND not already on the target build.
  const eligible = (r) => r.online && !isTarget(r.fwVersion);

  const toggle = (ip) => {
    const r = rows.find((x) => x.ip === ip);
    if (!r || !eligible(r)) return;
    setSelected((s) => ({ ...s, [ip]: !s[ip] }));
  };
  const selectAllEligible = () => {
    const next = {};
    rows.forEach((r) => { if (eligible(r)) next[r.ip] = true; });
    setSelected(next);
  };
  const selectNone = () => setSelected({});

  const selectedIps = rows.filter((r) => eligible(r) && selected[r.ip]).map((r) => r.ip);

  const startDowngrade = async () => {
    if (selectedIps.length === 0) return;
    if (!window.confirm(`Downgrade ${selectedIps.length} gateway(s) to ${TARGET_FW}? They will reboot.`)) return;
    setError(null);
    try {
      const res = await axios.post('/lowes-gateway/push', { ips: selectedIps });
      pollJob(res.data.jobId);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start downgrade');
    }
  };

  const pollJob = (jobId) => {
    const tick = async () => {
      try {
        const res = await axios.get(`/lowes-gateway/push/${jobId}`);
        const j = res.data.job;
        setJob(j);
        const done = j.status === 'completed' ||
          !j.targets.some((t) => ACTIVE_STATUSES.includes(t.status));
        if (done && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          // Auto-refresh the state table so the Current Firmware column reflects
          // the result without the user re-running Get State.
          probeState({ silent: true, keepSelection: true });
        }
      } catch (_) { /* keep polling */ }
    };
    if (pollRef.current) clearInterval(pollRef.current);
    tick();
    pollRef.current = setInterval(tick, 3000);
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const enteredCount = ipText.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean).length;
  const onlineCount = rows.filter((r) => r.online).length;
  const eligibleCount = rows.filter(eligible).length;

  return (
    <ProtectedComponent functionCode="OP13" functionName="Lowe's Gateway FW Revert">
      <div className="lowes-gw">
        <div className="lowes-gw-shell">

          {/* Header */}
          <header className="lowes-gw-head">
            <div className="lowes-gw-head-main">
              <span className="lowes-gw-eyebrow">Firmware Ops · Newton Standard</span>
              <h1 className="lowes-gw-title">Lowe's Gateway FW Revert</h1>
            </div>
            <div className="lowes-gw-target" title={FW_LABEL}>
              <span className="lowes-gw-target-label">TARGET BUILD</span>
              <span className="lowes-gw-target-fw">{FW_LABEL}</span>
            </div>
          </header>

          <div className="lowes-gw-warn">
            <span className="lowes-gw-warn-dot" />
            Downgrading <strong>reboots live gateways</strong>. Confirm your IP list before running.
          </div>

          {/* Step 1 — IPs */}
          <section className="lowes-gw-card">
            <div className="lowes-gw-step">
              <span className="lowes-gw-step-no">1</span>
              <h2 className="lowes-gw-step-title">Gateway IPs</h2>
              <span className="lowes-gw-step-hint">{enteredCount} entered</span>
            </div>
            <textarea
              className="lowes-gw-textarea"
              value={ipText}
              onChange={(e) => setIpText(e.target.value)}
              placeholder={'Paste gateway IPs — one per line or comma-separated\n\nex:\n192.0.2.10\n192.0.2.11, 192.0.2.12'}
              spellCheck={false}
              rows={7}
            />
            <div className="lowes-gw-actions">
              <button className="lowes-gw-btn primary" onClick={getState} disabled={loadingState || !ipText.trim()}>
                {loadingState ? <span className="lowes-gw-spin" /> : null}
                {loadingState ? 'Checking…' : 'Get State'}
              </button>
            </div>
          </section>

          {error && (
            <div className="lowes-gw-error">
              <span className="lowes-gw-error-icon">!</span>{error}
            </div>
          )}

          {/* Step 2 — Select */}
          {rows.length > 0 && (
            <section className="lowes-gw-card">
              <div className="lowes-gw-step">
                <span className="lowes-gw-step-no">2</span>
                <h2 className="lowes-gw-step-title">Select gateways</h2>
                <span className="lowes-gw-step-hint">{onlineCount}/{rows.length} online · {eligibleCount} eligible</span>
                <div className="lowes-gw-select-actions">
                  <button className="lowes-gw-link" onClick={selectAllEligible}>all eligible</button>
                  <span className="lowes-gw-sep">/</span>
                  <button className="lowes-gw-link" onClick={selectNone}>none</button>
                </div>
              </div>
              <div className="lowes-gw-tablewrap">
                <table className="lowes-gw-table">
                  <thead>
                    <tr>
                      <th className="col-check"></th>
                      <th className="col-state">State</th>
                      <th>IP Address</th>
                      <th>MAC Address</th>
                      <th>Current Firmware</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr
                        key={r.ip}
                        className={`${r.online ? '' : 'offline'} ${isTarget(r.fwVersion) ? 'on-target' : ''} ${selected[r.ip] ? 'is-selected' : ''}`}
                        onClick={() => eligible(r) && toggle(r.ip)}
                      >
                        <td className="col-check">
                          <input
                            type="checkbox"
                            className="lowes-gw-check"
                            checked={!!selected[r.ip]}
                            disabled={!eligible(r)}
                            title={isTarget(r.fwVersion) ? 'Already on target — nothing to downgrade' : (r.online ? '' : 'Offline')}
                            onChange={() => toggle(r.ip)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="col-state">
                          <span className={`lowes-gw-bubble ${r.online ? 'online' : 'offline'}`} title={r.error || (r.online ? 'online' : 'unreachable')} />
                          <span className="lowes-gw-statelabel">{r.online ? 'online' : 'offline'}</span>
                        </td>
                        <td className="mono ip">{r.ip}</td>
                        <td className="mono mac">{r.mac || '—'}</td>
                        <td className="mono fw">
                          {r.online ? (r.fwVersion || '—') : '—'}
                          {isTarget(r.fwVersion) && <span className="lowes-gw-tag">on target</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="lowes-gw-danger">
                <div className="lowes-gw-danger-meta">
                  <span className="lowes-gw-danger-count">{selectedIps.length}</span>
                  <span className="lowes-gw-danger-label">
                    selected · reboots on downgrade to <strong>{TARGET_FW}</strong>
                  </span>
                </div>
                <button className="lowes-gw-btn danger" onClick={startDowngrade} disabled={selectedIps.length === 0}>
                  Downgrade Selected
                </button>
              </div>
            </section>
          )}

          {/* Step 3 — Progress */}
          {job && (
            <section className="lowes-gw-card">
              <div className="lowes-gw-step">
                <span className="lowes-gw-step-no">3</span>
                <h2 className="lowes-gw-step-title">Downgrade progress</h2>
                <span className={`lowes-gw-jobstate ${job.status === 'completed' ? 'done' : 'running'}`}>
                  {job.status === 'completed' ? 'complete' : 'running'}
                </span>
              </div>
              <div className="lowes-gw-tablewrap">
                <table className="lowes-gw-table">
                  <thead>
                    <tr><th>IP Address</th><th>Status</th><th>Message</th></tr>
                  </thead>
                  <tbody>
                    {job.targets.map((t) => (
                      <tr key={t.ip}>
                        <td className="mono ip">{t.ip}</td>
                        <td><span className={`lowes-gw-pill s-${t.status}`}>{t.status}</span></td>
                        <td className="msg">{t.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

        </div>
      </div>
    </ProtectedComponent>
  );
}

export default LowesGatewayFwRevert;
